import { useParams } from 'react-router-dom';
import {
  useGetProjectByIdQuery,
  useGetZohoEmailSetupMutation,
  useUpdateProjectMutation,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { numberScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import moment from 'moment/moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { EditZohoEmailAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoEmailIntegrationSettings/EditZohoEmailAutoSetupSettings/EditZohoEmailAutoSetupSettings';
import { EditZohoEmailAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoEmailIntegrationSettings/EditZohoEmailAdvancedSettings/EditZohoEmailAdvancedSettings';
import { EditZohoEmailApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoEmailIntegrationSettings/EditZohoEmailApiDetailsSettings/EditZohoEmailApiDetailsSettings';
import { Skeleton } from 'src/components/ui/skeleton';

const AccordionIcon = ({ open }) => (
  <svg
    className={clsx('transition-transform', open && 'rotate-180')}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M12.6523 0.651864L6.35244 6.65186L0.652531 0.65187"
      stroke="#7A6BE5"
      strokeWidth="1.79997"
    />
  </svg>
);

const LoadingBlock = () => (
  <div className={'w-full flex flex-col gap-2.5 p-2.5 rounded-sm-5'}>
    <Skeleton className={'w-full h-12'} />
    <Skeleton className={'w-full h-12'} />
    <Skeleton className={'w-full h-12'} />
  </div>
);

export const EditZohoEmailIntegrationSettings = ({ disabled = false, integration, onSubmit }) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const duration = currentProject?.autoclose_time_window_minutes
    ? moment.duration(currentProject?.autoclose_time_window_minutes, 'minutes')
    : 0;
  const autocloseHours = duration ? Math.floor(duration.asHours()) : 0;
  const autocloseMinutes = duration ? duration.minutes() : 0;
  const [updateProject, { isLoading: isLoadingUpdateProject }] = useUpdateProjectMutation();
  const [code, setCode] = React.useState('');
  const [getZohoEmailSetup, { data: setupResponse, isLoading: isLoadingZohoEmailSetup }] =
    useGetZohoEmailSetupMutation() as any;

  useEffect(() => {
    if (id && integration.id) {
      getZohoEmailSetup({
        projectId: id,
        integrationId: integration.id,
      });
    }
  }, [id, integration]);

  useEffect(() => {
    if (setupResponse && setupResponse.custom_function_code) {
      setCode(setupResponse.custom_function_code);
    }
  }, [setupResponse]);

  const methods = useForm<any>({
    defaultValues: {
      name: integration?.integration_data?.name,
      client_id: integration?.integration_data?.client_id,
      client_secret: integration?.integration_data?.client_secret,
      scope: integration?.integration_data?.scope || 'Desk.tickets.ALL,Desk.basic.READ',
      server_locale: integration?.integration_data?.server_locale,
      integration_method: integration?.integration_data?.token ? 'token' : 'code',
      code: integration?.integration_data?.code,
      refresh_token: integration?.integration_data?.refresh_token,
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
    },
    resolver: yupResolver(
      object({
        name: stringScheme({ required: false }).label('Integration Name'),
        client_id: stringScheme({ required: false }).label('Client ID'),
        client_secret: stringScheme({ required: false }).label('Client secret'),
        scope: stringScheme({ required: false }).label('Scope'),
        server_locale: stringScheme({ required: false }).label('Server locale'),
        code: stringScheme({ required: false }).label('Code'),
        refresh_token: stringScheme({ required: false }).label('Refresh Token'),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
      }),
    ),
  });

  const submit = async (data) => {
    const result = {
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        name: currentProject.name,
        client_id: data.client_id,
        client_secret: data.client_secret,
        scope: data.scope,
        server_locale: data.server_locale,
        code: data.code,
        refresh_token: data.refresh_token,
      },
    };

    const autocloseMinutes = moment
      .duration(data.autocloseHours, 'hours')
      .add(data.autocloseMinutes, 'minutes')
      .asMinutes();

    const isAutocloseChanged = currentProject?.autoclose_time_window_minutes !== autocloseMinutes;

    if (isAutocloseChanged) {
      await updateProject({
        projectId: currentProject.id,
        name: currentProject?.name,
        rate_limit_project: currentProject?.rate_limit_project,
        rate_limit_project_per: currentProject?.rate_limit_project_per,
        rate_limit_chat: currentProject?.rate_limit_chat,
        rate_limit_chat_per: currentProject?.rate_limit_chat_per,
        rate_limit_max_chats: currentProject?.rate_limit_max_chats,
        rate_limit_max_chats_per: currentProject?.rate_limit_max_chats_per,
        cocbi_project_id: currentProject?.cocbi_project_id,
        use_cocbi_model: currentProject?.use_cocbi_model,
        ai_reply_generation: currentProject?.ai_reply_generation,
        ai_text_lense: currentProject?.ai_text_lense,
        autoclose_time_window_minutes: autocloseMinutes ? autocloseMinutes : null,
      });
    }
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (data.integration_method === 'code') {
      delete result.integration_data.refresh_token;
    } else {
      delete result.integration_data.code;
    }

    if (!result.integration_data.code) {
      delete result.integration_data.code;
    }

    if (!result.integration_data.refresh_token) {
      delete result.integration_data.refresh_token;
    }

    if (!result.integration_data.server_locale) {
      delete result.integration_data.server_locale;
    }

    if (!result.integration_data.client_secret) {
      delete result.integration_data.client_secret;
    }

    onSubmit(result);
  };

  return isLoadingUpdateProject || isLoadingZohoEmailSetup ? (
    <LoadingBlock />
  ) : (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="auto-setup-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Setup settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('web-widget-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoEmailAutoSetupSettings code={code} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoEmailAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoEmailApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
